import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { notifyToast } from 'utils/responseUtils'
import { useDispatch } from 'react-redux'
import { Upload } from 'lucide-react'

const FileUpload = ({
  itemKey,
  isMobile,
  uploadFile,
  filesToAccept,
  isSingleFile,
  value,
  className = '',
  onDropExtraAction,
  fileInputRef,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [dragActive, setDragActive] = useState(false)

  const handleChange = function (e, singleFile = false) {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      uploadFile(e.dataTransfer.files, itemKey, singleFile, e)
    }
    e.target.value = ''
  }

  const handleDrop = function (e, singleFile = false) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const { url } = getDraggedImageData(e)

    if (url && onDropExtraAction) {
      onDropExtraAction(url, itemKey, singleFile)
    }

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      if (singleFile) {
        if (e.dataTransfer.files.length == 1) {
          uploadFile(e.dataTransfer.files, itemKey, singleFile, e)
        } else {
          dispatch(notifyToast('error', 'Drop failed: You can only drop single file'))
        }
      } else {
        uploadFile(e.dataTransfer.files, itemKey, singleFile, e)
      }
    }
    e.target.value = ''
  }

  const handleDrag = function (e) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const getDraggedImageData = (e) => {
    const draggedData = e.dataTransfer.getData('text/plain')
    return draggedData ? JSON.parse(draggedData) : {}
  }

  return (
    <div
      className={`w-full relative flex items-center justify-center rounded-lg border border-dashed border-gray-300 transition-all duration-200 mb-3 ${
        dragActive ? 'bg-gray-50 border-blue-400' : 'bg-white hover:bg-gray-50'
      } ${value ? 'h-24' : 'h-36'} ${className}`}
      onDragEnter={handleDrag}
      onClick={() => fileInputRef.current?.click()}
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <input
          ref={fileInputRef}
          type="file"
          id={`${itemKey}-file-upload`}
          onChange={(e) => handleChange(e, isSingleFile)}
          multiple={!isSingleFile}
          accept={filesToAccept}
          className="hidden"
        />
        <label htmlFor={`${itemKey}-file-upload`} className="cursor-pointer w-full h-full">
          <div className="inline-flex flex-col gap-2 items-center">
            <Upload className="w-6 h-6 text-gray-400" />
            <p className="text-xs text-gray-500 font-medium text-center">
              {!isMobile && <span className="block mb-1">{t('model.drag-and-drop')}</span>}
              <span className="text-blue-500 font-semibold">
                {isMobile ? t('model.upload-from-gallery') : t('model.browse')}
              </span>
              <input
                type="file"
                ref={fileInputRef}
                accept={filesToAccept}
                className="hidden"
                multiple={!isSingleFile}
                onChange={(e) => {
                  uploadFile(e.target.files, itemKey, isSingleFile, e)
                }}
              />
              <span className="block text-gray-400 text-[10px] mt-1 font-medium">{t('common.max-file-size')}</span>
            </p>
          </div>
        </label>
        {dragActive && (
          <div
            className="drag-container absolute z-10 w-full h-full rounded-lg top-0 right-0 bottom-0 left-0"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={(e) => handleDrop(e, isSingleFile)}
          ></div>
        )}
      </form>
    </div>
  )
}

export default FileUpload
